"use client";
import React, { useCallback, useMemo, useState } from "react";
import Select from "antd/es/select";
import Text from "antd/es/typography/Text";
import { Button, Card, Divider, Flex, List, Space } from "antd";
import OptionCheckBox from "./OptionsCheckbox";
import ExtraOptionsModal from "./ExtraOptionsModal";
import MultipleServicesModal from "./MultipleServicesModal";
import type { ServiceDTO } from "@openings/api-client";
import { getMinBookingNoticeNote, maxMinBookingNotice } from "@utils/core";
import { resolveCatalogRequirementLabel } from "@openings/booking-core";
import { DefaultService, SelectedService } from "./types";
import {
  bookingSectionTitleStyle,
  bookingSmallStyle,
  bookingBodyStyle,
} from "../../bookingStyles";

const CloseIcon = () => (
  <svg viewBox="64 64 896 896" width="1em" height="1em" fill="currentColor">
    <path d="M563.8 512l262.5-312.9c4.4-5.2.7-13.1-6.1-13.1h-79.8c-4.7 0-9.2 2.1-12.3 5.7L511.6 449.8 295.1 191.7c-3-3.6-7.5-5.7-12.3-5.7H203c-6.8 0-10.5 7.9-6.1 13.1L459.4 512 196.9 824.9A7.95 7.95 0 00203 838h79.8c4.7 0 9.2-2.1 12.3-5.7l216.5-258.1 216.5 258.1c3 3.6 7.5 5.7 12.3 5.7h79.8c6.8 0 10.5-7.9 6.1-13.1L563.8 512z" />
  </svg>
);

const PlusCircleIcon = () => (
  <svg viewBox="64 64 896 896" width="1em" height="1em" fill="currentColor">
    <path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm0 820c-205.4 0-372-166.6-372-372s166.6-372 372-372 372 166.6 372 372-166.6 372-372 372z" />
    <path d="M482 432h60c4.4 0 8-3.6 8-8V296c0-4.4-3.6-8-8-8h-60c-4.4 0-8 3.6-8 8v128c0 4.4 3.6 8 8 8zm-2 136h64c4.4 0 8-3.6 8-8V500c0-4.4-3.6-8-8-8h-64c-4.4 0-8 3.6-8 8v60c0 4.4 3.6 8 8 8z" />
    <path d="M696 480H544V328c0-4.4-3.6-8-8-8h-48c-4.4 0-8 3.6-8 8v152H328c-4.4 0-8 3.6-8 8v48c0 4.4 3.6 8 8 8h152v152c0 4.4 3.6 8 8 8h48c4.4 0 8-3.6 8-8V544h152c4.4 0 8-3.6 8-8v-48c0-4.4-3.6-8-8-8z" />
  </svg>
);

const CUSTOM_VALUE = "__custom__";

const SelectService = ({
  showPrice,
  exactTerms = false,
  selectedServices,
  defaultServices,
  setServices,
  variant = "full",
  label = "Services",
  inheritPopupTheme = false,
}: {
  showPrice?: boolean;
  /**
   * The services carry one provider's own effective terms (the member page),
   * so each one's requirement badge is exact. Otherwise they are business
   * catalog rows and a payment is disclosed conditionally
   * (`resolveCatalogRequirementLabel`).
   */
  exactTerms?: boolean;
  defaultServices: DefaultService[];
  selectedServices: SelectedService[];
  setServices: (services: SelectedService[]) => void;
  variant?: "full" | "compact";
  /** Section heading. Defaults to "Services". */
  label?: string;
  /** Keep the popup inside the surrounding theme root so CSS vars inherit. */
  inheritPopupTheme?: boolean;
}) => {
  const [activeOptionIndex, setActiveOptionIndex] = useState<
    number | undefined
  >();
  const [isCustomServiceModalActive, setCustomServiceModalActive] =
    useState(false);

  // booking.requirement.*: each service states its request/payment
  // requirement where it is chosen, before a time or contact details
  // (BF-DRIFT-005). Keyed by id for the dropdown's optionRender.
  const requirementById = useMemo(
    () =>
      new Map(
        defaultServices.map((service) => [
          service.id,
          resolveCatalogRequirementLabel([service], { exactTerms }),
        ]),
      ),
    [defaultServices, exactTerms],
  );

  const selectOptions = useMemo(() => {
    const items: Array<{ value: string; label: React.ReactNode }> =
      defaultServices.map((s) => {
        // Single-provider surfaces (member page) pass showPrice so the
        // provider's own price rides along with the title. Location-level
        // surfaces hide it — prices differ per provider there.
        const price =
          showPrice && typeof s.price === "number" && s.price > 0
            ? `$${s.price}${s.up ? "+" : ""}`
            : null;
        return {
          value: s.id,
          label: price ? (
            <span>
              {s.title}
              <span
                style={{
                  color: "var(--openings-color-text-secondary, #666)",
                }}
              >
                {" · "}
                {price}
              </span>
            </span>
          ) : (
            s.title
          ),
        };
      });
    if (defaultServices.length > 1) {
      items.push({ value: CUSTOM_VALUE, label: "Custom" });
    }
    return items;
  }, [defaultServices, showPrice]);

  const findService = useCallback(
    (id: string) => defaultServices.find((s) => s.id === id),
    [defaultServices],
  );

  const handlePrimaryServiceChange = useCallback(
    (value: string) => {
      if (value === CUSTOM_VALUE) {
        setCustomServiceModalActive(true);
        return;
      }
      const svc = findService(value);
      if (svc) setServices([{ ...svc }]);
    },
    [findService, setServices],
  );

  const handleOptionApply = useCallback(
    (index: number, option?: SelectedService["option"]) => {
      const updated = [...selectedServices];
      updated[index] = {
        ...updated[index],
        option: option ? { ...option } : undefined,
      };
      setServices(updated);
      setActiveOptionIndex(undefined);
    },
    [selectedServices, setServices],
  );

  const handleCombineServices = useCallback(
    (services: ServiceDTO[]) => {
      setServices(services);
      setCustomServiceModalActive(false);
    },
    [setServices],
  );

  // booking.service.minNoticeNote: said before a time is chosen. The API
  // already leaves the starts that are too soon out of the openings.
  const minNoticeNote = getMinBookingNoticeNote(
    maxMinBookingNotice(
      selectedServices.map((service) => service.minBookingNoticeMinutes),
    ),
  );

  const clearToDefaultSingleService = useCallback(() => {
    if (defaultServices[0]) setServices([{ ...defaultServices[0] }]);
  }, [defaultServices, setServices]);

  const ServiceList = () => (
    <Card size="small">
      <List
        size="small"
        dataSource={selectedServices}
        renderItem={(item, index) => (
          <List.Item
            actions={
              item.options && item.options.length > 0
                ? [
                    <Button
                      key="opts"
                      onClick={() => setActiveOptionIndex(index)}
                      type="link"
                      size="small"
                    >
                      options
                    </Button>,
                  ]
                : []
            }
          >
            <List.Item.Meta
              title={
                <div
                  style={{ display: "flex", alignItems: "baseline", gap: 8 }}
                >
                  <span style={bookingBodyStyle}>{item.title}</span>
                  {showPrice &&
                  !item.option &&
                  typeof item.price === "number" &&
                  item.price > 0 ? (
                    <>
                      <Divider orientation="vertical" />
                      <span style={bookingSmallStyle}>
                        ${item.price}
                        {item.up ? "+" : ""}
                      </span>
                    </>
                  ) : null}
                </div>
              }
              description={
                item.option ? (
                  <div
                    style={{ display: "flex", alignItems: "baseline", gap: 8 }}
                  >
                    <span style={bookingSmallStyle}>{item.option.title}</span>
                    {showPrice &&
                    typeof item.option.price === "number" &&
                    item.option.price > 0 ? (
                      <>
                        <Divider orientation="vertical" />
                        <span style={bookingSmallStyle}>
                          ${item.option.price}
                        </span>
                      </>
                    ) : null}
                  </div>
                ) : undefined
              }
            />
          </List.Item>
        )}
      />
    </Card>
  );

  const SingleServicePicker = () => {
    const primaryService = selectedServices[0];
    const primaryValue = primaryService?.id ?? "";

    return (
      <>
        <style>
          {`.booking-service-select.ant-select .ant-select-selector {
              border-color: var(--openings-color-border, #e5e5e5) !important;
            }
            .booking-service-select.ant-select:not(.ant-select-disabled):hover .ant-select-selector,
            .booking-service-select.ant-select-focused:not(.ant-select-disabled) .ant-select-selector {
              border-color: var(--openings-booking-control-hover-border, var(--openings-color-primary, #111)) !important;
            }
            .booking-service-select.ant-select {
              height: var(--openings-booking-control-height, 40px);
            }
            .booking-service-select.ant-select .ant-select-selector {
              height: 100% !important;
            }
            /* Match the date input at 14px. The closed value renders as
               .ant-select-content-value (this antd build), not the legacy
               .ant-select-selection-item. Keep .ant-select-content at its native
               16px (lg) so its "\\a0" spacer keeps the 24px row and the input
               stays 40px tall; only shrink the visible value/placeholder to 14px
               and give it line-height = the row height so the smaller glyphs sit
               vertically centered. The inner search input is absolutely
               positioned and left at the global 16px (iOS zoom guard) — it
               renders no glyphs here, so 16px is invisible and never zooms. */
            .booking-service-select.ant-select .ant-select-content-value,
            .booking-service-select.ant-select .ant-select-selection-item,
            .booking-service-select.ant-select .ant-select-placeholder,
            .booking-service-select.ant-select .ant-select-selection-placeholder {
              font-size: var(--openings-booking-font-size, 14px);
              line-height: var(--select-font-height, 24px);
              font-family: inherit;
            }
            .booking-service-select-dropdown .ant-select-item-option-content {
              font-size: var(--openings-booking-font-size, 14px);
              font-family: inherit;
            }
            /* Match the date-nav chevrons: same secondary gray + 12px. */
            .booking-service-select.ant-select .ant-select-suffix {
              color: var(--openings-color-text-secondary, #666);
              font-size: 12px;
            }`}
        </style>
        <Select
          size="large"
          aria-label={label}
          options={selectOptions}
          value={primaryValue || undefined}
          onChange={handlePrimaryServiceChange}
          style={{ width: "100%" }}
          className="booking-service-select"
          classNames={{ popup: { root: "booking-service-select-dropdown" } }}
          getPopupContainer={
            inheritPopupTheme
              ? (triggerNode) =>
                  triggerNode.parentElement ?? document.body
              : undefined
          }
          placeholder="Select a service"
          popupStyle={{ zIndex: 10100 }}
          optionRender={(option) => {
            if (option.value === CUSTOM_VALUE) {
              return (
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <PlusCircleIcon />
                  <span>Custom</span>
                </div>
              );
            }
            const requirement = requirementById.get(String(option.value));
            return requirement ? (
              <span>
                {option.label}
                <span
                  data-booking-service-requirement
                  style={{
                    color: "var(--openings-color-text-secondary, #666)",
                  }}
                >
                  {" · "}
                  {requirement}
                </span>
              </span>
            ) : (
              option.label
            );
          }}
        />
      </>
    );
  };

  const MultipleServices = () => (
    <Card size="small">
      <Flex align="center" justify="space-between">
        <Text style={bookingSectionTitleStyle}>Services</Text>
        <Button type="link" onClick={() => setCustomServiceModalActive(true)}>
          Edit
        </Button>
      </Flex>
      <ServiceList />
    </Card>
  );

  const content =
    variant === "compact" ? (
      <Flex
        vertical
        gap={8}
        style={{
          gap: "var(--openings-booking-control-label-gap, 8px)",
        }}
      >
        <Text style={bookingSectionTitleStyle}>{label}</Text>
        <SingleServicePicker />
      </Flex>
    ) : (
      <Flex
        vertical
        gap={8}
        style={{
          gap: "var(--openings-booking-control-label-gap, 8px)",
        }}
      >
        <Text style={bookingSectionTitleStyle}>{label}</Text>
        {selectedServices.length > 1 ? (
          <MultipleServices />
        ) : (
          <Card size="small">
            <SingleServicePicker />
            <Divider />
            <ServiceList />
          </Card>
        )}
      </Flex>
    );

  return (
    <Flex vertical gap="small" style={{ width: "100%" }}>
      {content}

      {minNoticeNote ? (
        <Text
          data-booking-min-notice
          style={{ ...bookingSmallStyle, display: "block" }}
        >
          {minNoticeNote}
        </Text>
      ) : null}

      {activeOptionIndex !== undefined &&
      selectedServices[activeOptionIndex] ? (
        <ExtraOptionsModal
          service={selectedServices[activeOptionIndex]}
          showPrice={showPrice}
          onSubmit={(option) => handleOptionApply(activeOptionIndex, option)}
          onClose={() => setActiveOptionIndex(undefined)}
        />
      ) : null}

      {isCustomServiceModalActive ? (
        <MultipleServicesModal
          services={defaultServices}
          selectedServices={selectedServices}
          showPrice={showPrice}
          exactTerms={exactTerms}
          onSubmit={(services) => handleCombineServices(services)}
          onClose={() => setCustomServiceModalActive(false)}
        />
      ) : null}

      {selectedServices.length > 1 ? (
        <Button
          type="link"
          icon={<CloseIcon />}
          onClick={clearToDefaultSingleService}
        >
          Clear
        </Button>
      ) : null}

      {variant === "compact" &&
      selectedServices[0]?.options &&
      selectedServices[0]?.options.length > 0 ? (
        <Space orientation="vertical">
          <OptionCheckBox
            showPrice={showPrice}
            option={selectedServices[0].option}
            onOpenOptionModal={() => setActiveOptionIndex(0)}
          />
        </Space>
      ) : null}
    </Flex>
  );
};

export default SelectService;
